<template>
  <teleport to="body">
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2>Configura Grafici</h2>
        <button class="btn-close" @click="$emit('close')" title="Chiudi">×</button>
      </div>

      <div class="modal-body">
        <p class="modal-description">
          Aggiungi, rimuovi e ordina i grafici da visualizzare nella pagina Data.
          I grafici evidenziati sono obbligatori.
        </p>

        <!-- Sezione Grafici Selezionati (Ordinabili) -->
        <div class="section-group">
          <div class="section-header-row">
            <h3 class="section-title">Grafici Selezionati (Ordine di visualizzazione)</h3>
            <span class="count-badge" :class="{ 'warning': isLimitReached }">
              {{ localOrderedCharts.length }} / {{ MAX_CHARTS_LIMIT }}
            </span>
          </div>
          <div v-if="isLimitReached" class="limit-warning">
            ⚠️ Hai raggiunto il limite massimo di grafici. Rimuovi un grafico per aggiungerne un altro.
          </div>
          <div class="ordered-list">
            <div 
              v-for="(id, index) in localOrderedCharts" 
              :key="id" 
              class="order-item"
              :class="{ 'mandatory': isMandatory(id) }"
            >
              <div class="item-info">
                <span class="item-label">{{ getLabel(id) }}</span>
                <span v-if="isMandatory(id) && isCross(id)" class="badge-cross">Combined Default Setting</span>
                <span v-else-if="isMandatory(id)" class="badge-mandatory">Single Default Setting</span>
                <span v-else-if="isCross(id)" class="badge-cross">Combined</span>
                <span v-else class="badge-mandatory">Single</span>
              </div>
              <div class="item-actions">
                <button 
                  class="btn-icon" 
                  @click="move(index, 'up')" 
                  :disabled="index === 0"
                  title="Sposta su"
                >
                  <FontAwesomeIcon :icon="faArrowUp" />
                </button>
                <button 
                  class="btn-icon" 
                  @click="move(index, 'down')" 
                  :disabled="index === localOrderedCharts.length - 1"
                  title="Sposta giù"
                >
                  <FontAwesomeIcon :icon="faArrowDown" />
                </button>
                <button 
                  class="btn-icon btn-remove" 
                  @click="remove(id)" 
                  :disabled="isMandatory(id)"
                  title="Rimuovi"
                >
                  <FontAwesomeIcon :icon="faTrash" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Sezione Aggiungi Grafici -->
        <div class="section-group">
          <h3 class="section-title">Aggiungi Altri Grafici</h3>
          
          <div class="add-selectors">
            <div class="select-wrapper">
              <label>Grafici Singoli</label>
              <select v-model="selectedSensorToAdd" class="custom-select">
                <option value="">Seleziona sensore...</option>
                <option 
                  v-for="s in availableSensors" 
                  :key="s" 
                  :value="s"
                >
                  {{ SENSOR_LABELS[s] || s }}
                </option>
              </select>
              <button 
                class="btn-add" 
                @click="add(selectedSensorToAdd)" 
                :disabled="!selectedSensorToAdd || isLimitReached"
              >
                Aggiungi
              </button>
            </div>

            <div class="select-wrapper">
              <label>Grafici Combinati</label>
              <select v-model="selectedCrossToAdd" class="custom-select">
                <option value="">Seleziona combinato...</option>
                <option 
                  v-for="c in availableCross" 
                  :key="c.id" 
                  :value="c.id"
                >
                  {{ c.title }}
                </option>
              </select>
              <button 
                class="btn-add" 
                @click="add(selectedCrossToAdd)" 
                :disabled="!selectedCrossToAdd || isLimitReached"
              >
                Aggiungi
              </button>
            </div>
          </div>
        </div>

        <div class="quick-actions">
          <button class="btn-secondary" @click="resetToDefaults">
            Ripristina Default
          </button>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Annulla</button>
        <button class="btn-save" @click="save">Salva</button>
      </div>
    </div>
  </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useChartVisibility } from '@/composables/useChartVisibility';
import { SENSOR_LABELS, SENSOR_LIST, CROSS_LIST, MANDATORY_CHARTS, MAX_CHARTS_LIMIT } from '@/config/chartConfig';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowUp, faArrowDown, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';

// blocca scroll del body quando il modale è aperto
onMounted(() => {
  document.body.style.overflow = 'hidden';
});

onBeforeUnmount(() => {
  document.body.style.overflow = '';
});

const props = defineProps<{
  deviceType: 'static' | 'dynamic';
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved'): void;
}>();

const { preferences, resetToDefaults: resetPrefs } = useChartVisibility();

// Copia locale per permettere annullamento
const localOrderedCharts = ref<string[]>([...preferences.value.orderedCharts]);
const selectedSensorToAdd = ref("");
const selectedCrossToAdd = ref("");

onMounted(() => {
  localOrderedCharts.value = [...preferences.value.orderedCharts];
});

const isMandatory = (id: string) => MANDATORY_CHARTS.includes(id);
const isCross = (id: string) => CROSS_LIST.some(c => c.id === id);
const isLimitReached = computed(() => localOrderedCharts.value.length >= MAX_CHARTS_LIMIT);

const getLabel = (id: string) => {
  const cross = CROSS_LIST.find(c => c.id === id);
  if (cross) return cross.title;
  return SENSOR_LABELS[id] || id;
};

const availableSensors = computed(() => {
  return SENSOR_LIST.filter(s => !localOrderedCharts.value.includes(s));
});

const availableCross = computed(() => {
  return CROSS_LIST.filter(c => !localOrderedCharts.value.includes(c.id));
});

const add = (id: string) => {
  if (!id || isLimitReached.value) return;
  if (!localOrderedCharts.value.includes(id)) {
    localOrderedCharts.value.push(id);
  }
  selectedSensorToAdd.value = "";
  selectedCrossToAdd.value = "";
};

const remove = (id: string) => {
  if (isMandatory(id)) return;
  localOrderedCharts.value = localOrderedCharts.value.filter(item => item !== id);
};

const move = (index: number, direction: 'up' | 'down') => {
  const newIndex = direction === 'up' ? index - 1 : index + 1;
  if (newIndex < 0 || newIndex >= localOrderedCharts.value.length) return;
  
  const list = [...localOrderedCharts.value];
  const temp = list[index];
  list[index] = list[newIndex];
  list[newIndex] = temp;
  localOrderedCharts.value = list;
};

const save = () => {
  preferences.value.orderedCharts = [...localOrderedCharts.value];
  emit('saved');
  emit('close');
};

const resetToDefaults = () => {
  // Ripristiniamo i default nel composable e poi nella copia locale
  resetPrefs();
  localOrderedCharts.value = [...preferences.value.orderedCharts];
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;      /* mobile-safe */
  background: rgba(0, 0, 0, 0.75);

  display: flex;
  justify-content: center;

  /* su mobile è spesso meglio partire dall’alto */
  align-items: flex-start;

  padding: 16px;
  padding-top: max(16px, env(safe-area-inset-top));
  box-sizing: border-box;

  z-index: 99999;
}

@media (min-width: 768px) {
  .modal-overlay {
    align-items: center;
    padding-top: 20px;
  }
}

.modal-container {
  width: min(700px, 100%);
  max-height: calc(100dvh - 32px);
  margin: 0 auto;

  background: #1a1a1a;
  border-radius: 12px;
  border: 1px solid #2a2a2a;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #2a2a2a;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #e6e6e6;
}

.btn-close {
  background: transparent;
  border: none;
  color: #999;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* border-radius: 6px; */
  transition: all 0.2s ease;
}

.btn-close:hover {
  /* background: #2a2a2a; */
  color: #4db8a8;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

/* Custom Scrollbar for Modal Body */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-description {
  margin: 0 0 20px 0;
  color: #9fb0c2;
  font-size: 14px;
  line-height: 1.5;
}

.section-group {
  margin-bottom: 32px;
}

.section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #4db8a8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #2a2a2a;
}

.count-badge {
  font-size: 12px;
  background: #2a2a2a;
  padding: 4px 10px;
  border-radius: 20px;
  color: #9fb0c2;
  font-weight: 600;
}

.count-badge.warning {
  background: rgba(255, 165, 0, 0.1);
  color: #ffa500;
  border: 1px solid rgba(255, 165, 0, 0.2);
}

.limit-warning {
  background: rgba(255, 165, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.2);
  color: #ffa500;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ordered-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #222;
  border: 1px solid #333;
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.order-item.mandatory {
  border-left: 4px solid #4db8a8;
  background: rgba(77, 184, 168, 0.05);
}

.item-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.item-label {
  color: #e6e6e6;
  font-size: 14px;
  font-weight: 500;
}

.badge-mandatory, .badge-cross {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-mandatory {
  background: rgba(77, 184, 168, 0.2);
  color: #4db8a8;
}

.badge-cross {
  background: rgba(58, 123, 213, 0.2);
  color: #5da9ff;
}

.item-actions {
  display: flex;
  gap: 4px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  color: #e6e6e6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
}

.btn-icon:hover:not(:disabled) {
  background: #333;
  border-color: #4db8a8;
}

.btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btn-remove:hover:not(:disabled) {
  border-color: #ff4d4d;
  color: #ff4d4d;
}

.add-selectors {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.select-wrapper label {
  font-size: 13px;
  color: #9fb0c2;
  font-weight: 500;
}

.custom-select {
  padding: 10px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  color: #e6e6e6;
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.custom-select:focus {
  border-color: #4db8a8;
}

.btn-add {
  margin-top: 4px;
  padding: 8px;
  background: rgba(77, 184, 168, 0.1);
  border: 1px dashed #4db8a8;
  color: #4db8a8;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add:hover:not(:disabled) {
  background: rgba(77, 184, 168, 0.2);
}

.btn-add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #333;
  color: #666;
}

.quick-actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #2a2a2a;
}

.btn-secondary {
  padding: 8px 16px;
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  color: #e6e6e6;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #333;
  border-color: #4a4a4a;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #2a2a2a;
}

.btn-cancel,
.btn-save {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background: transparent;
  border: 1px solid #3a3a3a;
  color: #9fb0c2;
}

.btn-cancel:hover {
  background: #2a2a2a;
  border-color: #4a4a4a;
  color: #e6e6e6;
}

.btn-save {
  background: #4db8a8;
  border: 1px solid #4db8a8;
  color: #fff;
}

.btn-save:hover {
  background: #3da898;
  border-color: #3da898;
}

@media (max-width: 640px) {
  .add-selectors {
    grid-template-columns: 1fr;
  }
}
</style>
